import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import Debug from 'debug';
import { Subject } from 'rxjs';
import { STATUS, STATUS_CLASS } from '../../enums/status-enum';
import { ActionFooterService } from '../../layout/services/actionFooter.service';
import { IAumMenuItemPerRow, IAumMenuPerRowDelegate } from '../../pattern';
import { IAumMenuDelegate, IAumMenuItem } from '../../pattern/menu/menu';
import { AumPaginationActionBar } from '../../pattern/pagination-action-bar/pagination-action-bar';
import { DateLocalePipe } from '../../pipes/dateLocale.pipe';
import { IColumn } from './IColumn.interface';
import { ITableDelegate, ITableDelegateWithRowActions } from './ITableDelegate.interface';
import { ITableOptions } from './ITableOptions.interface';
import { SearchMode } from './SearchMode.enum';

const debug = Debug('aumico:aumico-lib-core-fe:TableComponent');

const AUMICO_TABLE_HOST_ATTRIBUTES = ['sub-table', 'expandable'];

@Component({
    selector: 'aumico-table',
    templateUrl: 'table.component.html',
    styleUrls: ['table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class TableComponent implements OnInit, OnDestroy, AfterViewInit, IAumMenuDelegate, IAumMenuPerRowDelegate {
    @ViewChildren('expandableRow', { read: ViewContainerRef }) containers;
    expandedRow: number;
    FILTER_VALUE;
    OPTIONS: ITableOptions;
    selectedRow: any;
    @Input() public set options(options: ITableOptions) {
        if (options) {
            this.OPTIONS = options;
            this.singleSelectionProperty = options.singleSelectionProperty;
            this.updateDatasource(this.DATA);
        }
    }
    SUBTABLE_OPTIONS: ITableOptions;
    @Input() public set subtableOptions(options: ITableOptions) {
        if (options) {
            this.SUBTABLE_OPTIONS = options;
        }
    }

    SUBTABLE_DATA: any;
    @Input() public set subtableData(data: any) {
        if (data !== undefined) {
            this.SUBTABLE_DATA = data;
        } else {
            this.SUBTABLE_DATA = this.NODATA;
        }
    }
    DATA: any;
    NODATA = [
        {
            data: 'No data to display.',
        },
    ];
    @Input() public set data(data: any) {
        if (data !== undefined) {
            this.DATA = data;
        } else {
            this.DATA = this.NODATA;
        }
        this.updateDatasource(this.DATA);
    }

    DELEGATE?: ITableDelegate;
    @Input() public set delegate(delegate: ITableDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    FILTER_SUBJECT?: Subject<string>;
    @Input() public set filterSubject(subject: Subject<string>) {
        if (this.FILTER_SUBJECT) {
            this.FILTER_SUBJECT.unsubscribe();
        }
        this.FILTER_SUBJECT = subject;
        if (subject) {
            subject.subscribe(this.applyFilter.bind(this));
        }
    }

    DETACH_PAGER?: boolean = false;
    @Input() public set detachPager(detach: boolean) {
        this.DETACH_PAGER = detach;
    }

    @Input() userPreferredNumberOfDocumentsPerPage: number | undefined;

    @Input() hideSearchBar = false;
    @Input() highlightRows = false;
    @Output() pageSizeChanged = new EventEmitter<number>();

    @ViewChild('paginator', { static: false })
    paginator: MatPaginator;
    @ViewChild('aumpaginator', { static: false }) pagingActionBar: AumPaginationActionBar;

    protected subscriptions = [];

    public headers: string[];
    public datasource: MatTableDataSource<any>;
    public checkboxSelection = new SelectionModel<any>(true, []);
    public favoriteSelection = new SelectionModel<any>(true, []);
    public tableTheme: string = '';

    @Input() singleSelection? = false;
    @Input() savedSingleSelectionId?: string;
    singleSelectionProperty: string | undefined;

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        public _elementRef: ElementRef,
        private resolver: ComponentFactoryResolver,
        @Optional() private actionFooterService: ActionFooterService,
        private dateLocalPipe: DateLocalePipe,
    ) {
        for (const attr of AUMICO_TABLE_HOST_ATTRIBUTES) {
            if (this._hasHostAttributes(attr)) {
                this.tableTheme = attr;
                (this._getHostElement() as HTMLElement).classList.add(attr);
            }
        }

        this.matIconRegistry.addSvgIcon(
            'aumico-pattern-icon-settings-vertical',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-icon-settings-vertical.svg'),
        );
        this.OPTIONS = {
            columns: [{ name: 'data', type: 'text', property: 'data' }],
        };
        this.headers = this.getColumns().map((column) => column.name);

        this.datasource = new MatTableDataSource<any>(this.NODATA);
    }
    onMenuItemPerRowClick(action: string, row: any, item?: IAumMenuItemPerRow, event?: Event): void {
        if (this.DELEGATE) {
            (this.DELEGATE as ITableDelegateWithRowActions).onMenuItemPerRowClick(action, row, item, event);
        }
    }

    onMenuItemClick(action: string, item?: IAumMenuItem, event?: Event): void {
        debug('onMenuItemClick', action, item, event);
        if (this.DELEGATE) {
            this.DELEGATE.onMenuItemClick(action, this.checkboxSelection);
        }
    }

    onMenuClick(): void {
        if (this.DELEGATE.onMenuClicked == null) {
            return;
        }
        this.DELEGATE.onMenuClicked(this.checkboxSelection.selected.length);
    }

    ngOnDestroy(): void {
        debug('movePagerToGlobalView detach:', null);
        if (this.actionFooterService && this.DETACH_PAGER) {
            this.actionFooterService.pagingActionBar$.next(null);
        }
    }

    ngOnInit(): void {
        debug(this.OPTIONS);
        debug(this.DATA);
        if (this.savedSingleSelectionId != null) {
            this.handleSavedSelection(this.savedSingleSelectionId);
        }
    }

    ngAfterViewInit() {
        this.datasource.paginator = this.paginator;
        debug('movePagerToGlobalView move:', this.pagingActionBar, this.actionFooterService, this.DETACH_PAGER);
        if (this.actionFooterService && this.DETACH_PAGER) {
            debug('movePagerToGlobalView move: dispatch event', this.pagingActionBar.elRef);
            this.actionFooterService.pagingActionBar$.next(this.pagingActionBar.elRef);
        }
    }

    private handleSavedSelection(savedSelectionId: string): void {
        this.datasource.data.forEach((row) => {
            if (row._id === savedSelectionId) {
                this.checkboxSelection.select(row);
            }
        });
    }

    public applyFilter(value: string) {
        debug('applyFilter', value);
        // overwriting filterPredicate function to handle filtering with formatted date
        this.datasource.filterPredicate = (data, filter) => {
            let result = false;
            for (const key of Object.keys(data)) {
                const column = this.OPTIONS.columns.find((column) => column.property === key);

                if (column == null || column.excludedFromSearch) {
                    continue;
                }
                if (column.type === 'date') {
                    const date = data[key] as string | Date;
                    // for dates we transform them with this.dateLocalPipe
                    if (date != undefined) {
                        const localDate = this.dateLocalPipe.transform(date)?.toString();
                        // search using 'start with'
                        if (column.searchMode === SearchMode.START_WITH) {
                            result = result || localDate.toLowerCase().startsWith(filter.trim().toLowerCase());
                            continue;
                        }

                        // search using 'includes'
                        result = result || localDate.toLowerCase().indexOf(filter.trim().toLowerCase()) !== -1;
                        continue;
                    }
                }

                const text = data[key] as string;
                if (text == undefined) {
                    continue;
                }
                // search using 'start with'
                if (column.searchMode === SearchMode.START_WITH) {
                    result = result || text.toString().trim().toLowerCase().startsWith(filter.trim().toLowerCase());
                    continue;
                }
                // search using 'includes'
                result = result || text.toString().trim().toLowerCase().indexOf(filter.trim().toLowerCase()) !== -1;
            }
            return result;
        };
        if (value == null) {
            return;
        }
        value = value.trim(); // Remove whitespace
        value = value.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.datasource.filter = value;
        this.FILTER_VALUE = value;
    }

    public onRowButtonClicked(id: string, action?: string): void {
        if (this.DELEGATE) {
            this.DELEGATE.onRowButtonClicked(id, action);
        }
    }

    public onSingleRowSelection(id: string, checked: boolean): void {
        if (this.DELEGATE && this.DELEGATE.onSingleRowSelection) {
            this.DELEGATE.onSingleRowSelection(id, checked);
        }
    }

    public getColumns(): IColumn[] {
        if (this.OPTIONS && this.OPTIONS.columns) {
            return this.OPTIONS.columns;
        } else {
            return [{ name: 'data', type: 'text', property: 'data' }];
        }
    }

    private updateDatasource(data: any[]) {
        this.headers = this.getColumns().map((col: IColumn) => col.name);
        // set datasource
        this.datasource = new MatTableDataSource<any>(data as any);
        this.datasource.paginator = this.paginator;
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllCheckboxesSelected() {
        const numSelected = this.checkboxSelection.selected.length;
        const numRows = this.datasource.filteredData.filter((row) => !row.disableSelection).length;

        return numSelected === numRows;
    }

    public handleSelection(event: any, row: any): void {
        if (this.singleSelection) {
            this.checkboxSelection.clear();
            this.savedSingleSelectionId = null;
            if (this.OPTIONS.singleSelectionProperty != null) {
                this.onSingleRowSelection(row[this.OPTIONS.singleSelectionProperty], event.checked);
            }
        }
        this.checkboxSelection.toggle(row);
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllCheckboxesSelected()
            ? this.checkboxSelection.clear()
            : this.datasource.filteredData.forEach((row) => {
                  if (!row.disableSelection) this.checkboxSelection.select(row);
              });
    }
    /** Gets whether the table has one of the given attributes. */
    private _hasHostAttributes(...attributes: string[]) {
        return attributes.some((attribute) => this._elementRef.nativeElement.hasAttribute(attribute));
    }

    _getHostElement() {
        return this._elementRef.nativeElement;
    }

    rowClicked(index: number, row: any) {
        if (this.tableTheme === 'expandable') {
            debug('expanded');
            if (this.expandedRow != null) {
                // clear old message
                this.containers.toArray()[this.expandedRow].clear();
            }

            if (this.expandedRow === index) {
                this.expandedRow = null;
            } else {
                this.expandedRow = index;
                const container = this.containers.toArray()[index];
                const factory: ComponentFactory<TableComponent> = this.resolver.resolveComponentFactory(TableComponent);
                const subTableComponent = container.createComponent(factory);

                subTableComponent.instance.tableTheme = 'sub-table';
                subTableComponent.instance.options = this.SUBTABLE_OPTIONS;
                subTableComponent.instance.data = this.SUBTABLE_DATA;
            }
        } else {
            debug('not expandable', row);
            if (this.OPTIONS.rowClickAction != null) {
                if (this.selectedRow === row && this.highlightRows) {
                    this.selectedRow = null;
                    return;
                } else {
                    this.selectedRow = row;
                    this.onRowButtonClicked(
                        row[this.OPTIONS.rowClickAction.property],
                        this.OPTIONS.rowClickAction.action,
                    );
                }
            }
        }
    }

    determineStatusClass(status: string): string {
        switch (status) {
            case STATUS.ACTIVE: {
                return STATUS_CLASS.ACTIVE;
            }
            case STATUS.WARNING: {
                return STATUS_CLASS.WARNING;
            }
            case STATUS.ERROR: {
                return STATUS_CLASS.ERROR;
            }
            case STATUS.ARCHIVED: {
                return STATUS_CLASS.ARCHIVED;
            }
            default: {
                return 'status-active';
            }
        }
    }

    public onPageSelectionChange(pageEvent: PageEvent): void {
        this.pageSizeChanged.emit(pageEvent.pageSize);
    }
}
